// Journal builders for changes made after a sale: an amended invoice, a cancellation settled,
// a refund, a destination charge, a voucher used, and a partner collecting a charge (06 §6.3).
// Pure and exact like `accounting.ts`: integer minor units, every builder returns lines that balance
// or throws an `AccountingError`.
import {
  AccountingError,
  PAYMENT_METHOD_ACCOUNT,
  assertBalanced,
  collectionLines,
  creditNoteLines,
  type CollectionInput,
  type Dimensions,
  type InvoiceLineForPosting,
  type JournalLineDraft,
  type WalletRef,
} from './accounting';
import { allocateMoney, money, type CurrencyCode } from './money';

function positive(value: number, label: string, allowZero = false): void {
  if (!Number.isSafeInteger(value) || value < 0 || (!allowZero && value === 0)) {
    throw new AccountingError(
      `${label} must be a ${allowZero ? 'non-negative' : 'positive'} whole number of minor units`,
    );
  }
}

const debit = (accountCode: string, amountMinor: number, dimensions: Dimensions) =>
  ({ accountCode, debitMinor: amountMinor, creditMinor: 0, dimensions }) satisfies JournalLineDraft;
const credit = (accountCode: string, amountMinor: number, dimensions: Dimensions) =>
  ({ accountCode, debitMinor: 0, creditMinor: amountMinor, dimensions }) satisfies JournalLineDraft;

function finish(lines: JournalLineDraft[]): JournalLineDraft[] {
  const kept = lines.filter((l) => l.debitMinor > 0 || l.creditMinor > 0);
  if (kept.length > 0) assertBalanced(kept);
  return kept;
}

// ------------------------------------------------------------------ amendment

export interface AmendmentInput extends Omit<CollectionInput, 'invoiceLines'> {
  readonly previous: readonly InvoiceLineForPosting[];
  readonly next: readonly InvoiceLineForPosting[];
}

/**
 * An amended invoice (06 §6.3, "Shipment amended"): the difference between the journal the old
 * invoice would post and the journal the new one posts, account by account. Everything already
 * posted plus this equals what the new invoice alone would have posted, to the minor unit, whatever
 * the VAT rounding does. No difference gives no lines.
 */
export function amendmentLines(input: AmendmentInput): JournalLineDraft[] {
  const before = collectionLines({ ...input, invoiceLines: input.previous });
  const after = collectionLines({ ...input, invoiceLines: input.next });
  const net = new Map<string, number>();
  const add = (l: JournalLineDraft, sign: 1 | -1) =>
    net.set(l.accountCode, (net.get(l.accountCode) ?? 0) + sign * signedDebit(l));
  for (const l of before) add(l, -1);
  for (const l of after) add(l, 1);
  const lines: JournalLineDraft[] = [];
  for (const [accountCode, delta] of [...net].sort(([a], [b]) => a.localeCompare(b))) {
    if (delta > 0) lines.push(debit(accountCode, delta, input.dimensions));
    if (delta < 0) lines.push(credit(accountCode, -delta, input.dimensions));
  }
  return finish(lines);
}

function signedDebit(l: JournalLineDraft): number {
  return l.debitMinor - l.creditMinor;
}

/**
 * Lowers an invoice by `amountMinor` in proportion to what it sold (freight, add-ons, boxes): the
 * lines a goodwill or dispute credit posts against. Discounts and charges that are not revenue of the
 * sender do not take part.
 */
export function proportionalCreditLines(
  invoiceLines: readonly InvoiceLineForPosting[],
  amountMinor: number,
  currency: CurrencyCode,
): InvoiceLineForPosting[] {
  positive(amountMinor, 'Credit');
  const totals = new Map<InvoiceLineForPosting['kind'], number>();
  for (const l of invoiceLines) {
    if (l.kind !== 'discount' && l.amountMinor > 0) {
      totals.set(l.kind, (totals.get(l.kind) ?? 0) + l.amountMinor);
    }
  }
  const kinds = [...totals.keys()];
  const sold = [...totals.values()].reduce((a, b) => a + b, 0);
  if (kinds.length === 0 || amountMinor > sold) {
    throw new AccountingError('The credit is more than the invoice sold');
  }
  const shares = allocateMoney(money(amountMinor, currency), [...totals.values()]);
  return kinds.map((kind, i) => ({
    kind,
    amountMinor: (shares[i] as { amountMinor: number }).amountMinor,
  }));
}

/** The mirror of the sale for a cancellation: Dr the revenue and VAT, Cr 1100 (and 4800 for a discount). */
export const cancellationLines = (input: CollectionInput): JournalLineDraft[] =>
  creditNoteLines(input);

// ------------------------------------------------------------ settling the money

export interface SettlementInput {
  /** What the customer had paid in all (Dr 1100: the receivable is now owed back). */
  readonly paidMinor: number;
  /** Money out of a wallet. */
  readonly refundMinor: number;
  readonly wallet?: WalletRef | undefined;
  /** Kept as customer credit (Cr 2010). */
  readonly creditMinor: number;
  /** Paid by voucher: the value goes back to the account the voucher was taken from (2020, or 4800). */
  readonly vouchers: readonly { readonly account: '2020' | '4800'; readonly amountMinor: number }[];
  readonly dimensions: Dimensions;
}

/**
 * A cancelled shipment's payments settled (06 §6.3): after the credit note the customer is owed what
 * they paid. Dr 1100 for all of it; Cr the wallet for the refund, Cr 2010 for what stays as credit,
 * Cr 2020 (or 4800) for what was paid with a voucher, which is restored.
 */
export function settlementLines(input: SettlementInput): JournalLineDraft[] {
  positive(input.paidMinor, 'Paid', true);
  positive(input.refundMinor, 'Refund', true);
  positive(input.creditMinor, 'Credit', true);
  const voucherMinor = input.vouchers.reduce((s, v) => s + v.amountMinor, 0);
  if (input.refundMinor + input.creditMinor + voucherMinor !== input.paidMinor) {
    throw new AccountingError(
      'The refund, the credit and the vouchers must add up to what was paid',
    );
  }
  if (input.refundMinor > 0 && !input.wallet) {
    throw new AccountingError('A refund of money needs the wallet it leaves from');
  }
  const d = input.dimensions;
  return finish([
    debit('1100', input.paidMinor, d),
    credit(
      input.wallet?.accountCode ?? '1000',
      input.refundMinor,
      input.wallet ? { ...d, walletId: input.wallet.walletId } : d,
    ),
    credit('2010', input.creditMinor, d),
    ...input.vouchers.map((v) => credit(v.account, v.amountMinor, d)),
  ]);
}

export interface RefundInput {
  /** Taken from the shipment's overpayment (Dr 1100). */
  readonly receivableMinor: number;
  /** Taken from the customer's credit (Dr 2010). */
  readonly depositMinor: number;
  /** Where it goes: a wallet, or back to the customer's credit. */
  readonly wallet?: WalletRef | undefined;
  readonly dimensions: Dimensions;
}

/** A refund (06 §6.3): Dr 1100 and/or 2010; Cr the wallet's account, or 2010 when it stays as credit. */
export function refundLines(input: RefundInput): JournalLineDraft[] {
  positive(input.receivableMinor, 'Refund from the shipment', true);
  positive(input.depositMinor, 'Refund from customer credit', true);
  const total = input.receivableMinor + input.depositMinor;
  if (total === 0) throw new AccountingError('A refund needs an amount');
  const d = input.dimensions;
  if (!input.wallet) {
    if (input.depositMinor > 0) {
      throw new AccountingError('Customer credit cannot be refunded as customer credit');
    }
    return finish([debit('1100', total, d), credit('2010', total, d)]);
  }
  return finish([
    debit('1100', input.receivableMinor, d),
    debit('2010', input.depositMinor, d),
    credit(input.wallet.accountCode, total, { ...d, walletId: input.wallet.walletId }),
  ]);
}

export interface VoucherRestoreInput {
  readonly amountMinor: number;
  readonly account: '2020' | '4800';
  readonly dimensions: Dimensions;
}

/** A voucher payment taken back: the receivable is owed again (Dr 1100) and the voucher's account is credited. */
export function voucherRestoreLines(input: VoucherRestoreInput): JournalLineDraft[] {
  positive(input.amountMinor, 'Voucher value');
  return finish([
    debit('1100', input.amountMinor, input.dimensions),
    credit(input.account, input.amountMinor, input.dimensions),
  ]);
}

/**
 * The account a voucher is paid out of: points that were spent already sit in 2020 (Dr 4800 / Cr 2020
 * when they were confirmed); a voucher from a ticket, a campaign or staff never did, so its value is a
 * discount when it is used (4800).
 */
export function voucherDebitAccount(source: string): '2020' | '4800' {
  return source === 'points' ? '2020' : '4800';
}

/** Method `voucher` is a payment line whose debit side is not an asset (06 §6.3). */
export const VOUCHER_PAYMENT_ACCOUNT = PAYMENT_METHOD_ACCOUNT['voucher'];

// ------------------------------------------------------- destination charges

/** Duties and taxes are passed through to customs (2040); every other destination charge is revenue (4010). */
export function destinationChargeAccount(kind: string): '4010' | '2040' {
  return kind === 'customs_duty' ? '2040' : '4010';
}

export interface DestinationChargeInput {
  readonly amountMinor: number;
  readonly creditAccount: '4010' | '2040';
  readonly dimensions: Dimensions;
}

/** A destination charge booked by the destination entity (06 §6.3): Dr 1100 (the consignee owes it), Cr 4010 or 2040. */
export function destinationChargeLines(input: DestinationChargeInput): JournalLineDraft[] {
  positive(input.amountMinor, 'Charge');
  return finish([
    debit('1100', input.amountMinor, input.dimensions),
    credit(input.creditAccount, input.amountMinor, input.dimensions),
  ]);
}

export interface PartnerChargeCollectionInput {
  /** All the cash the partner collected. */
  readonly amountMinor: number;
  /** The part of it that settles destination charges already booked (Cr 1100). */
  readonly receivableMinor: number;
  /** Where the rest is credited (4010, or 2040 for duties), as before charges could be booked. */
  readonly creditAccount: '4010' | '2040';
  readonly dimensions: Dimensions;
}

/**
 * Cash a partner collected at the door (12 §12.6): Dr 1003 until it is remitted. The part that settles
 * a charge booked with `addDestinationCharge` clears that receivable (Cr 1100) instead of booking the
 * revenue a second time; any rest is booked as before (Cr 4010 or 2040).
 */
export function partnerChargeCollectionLines(
  input: PartnerChargeCollectionInput,
): JournalLineDraft[] {
  positive(input.amountMinor, 'Amount collected');
  positive(input.receivableMinor, 'Receivable settled', true);
  if (input.receivableMinor > input.amountMinor) {
    throw new AccountingError('The receivable settled cannot be more than the cash collected');
  }
  const d = input.dimensions;
  return finish([
    debit('1003', input.amountMinor, d),
    credit('1100', input.receivableMinor, d),
    credit(input.creditAccount, input.amountMinor - input.receivableMinor, d),
  ]);
}

/**
 * How much of a collection settles booked charges: the charges still outstanding, never more than
 * the cash. `outstandingMinor` is what was booked less what was collected so far.
 */
export function receivableSettled(collectedMinor: number, outstandingMinor: number): number {
  return Math.max(0, Math.min(collectedMinor, outstandingMinor));
}
